// useTreeFocus — pans + zooms the org tree so the current user's card lands
// near the top-centre of the viewport.
//
// The bias is intentional: the user's manager chain should be visible above
// them, and their first ~2 levels of reports should fit below. We achieve
// this by translating so the user's node sits at roughly the 25% mark down
// from the top, rather than dead-centre.
//
// One-shot semantics: the effect runs once per `(currentUserUpn,
// layout-signature)` pair. After that the user can pan/zoom freely without
// the hook fighting their interactions. A new layout (e.g. data refresh) or
// a new current user re-arms the focus.
//
// When `currentUserUpn` is null the hook no-ops.
//
// See PRODOPSAI-469.

import { useEffect, useRef } from 'react';
import type { HierarchyPointNode } from 'd3-hierarchy';
import type { IOrgGraphNode } from '@skystone/shared';
import { VIRTUAL_ROOT_ID } from './useTreeLayout';

/** Zoom level used when focusing the current user. Slightly zoomed-out so
 *  the manager chain + reports both fit in the initial framing. Exported
 *  so US5's "Centre on me" + search-navigation helpers compose the same
 *  framing math. */
export const FOCUS_ZOOM = 0.8;
/** Fraction of viewport height to leave above the focused node, so the
 *  manager chain is visible. 0.25 means the node sits ~25% from the top.
 *  Exported for the same reason as FOCUS_ZOOM. */
export const TOP_BIAS = 0.25;

export interface UseTreeFocusParams {
  /** The laid-out tree root, or null when the layout has not produced a
   *  tree yet (empty data, all orphans, etc.). */
  layout: HierarchyPointNode<IOrgGraphNode> | null;
  /** UPN of the signed-in user, or null when no user is identified. When
   *  null the hook falls back to framing the tree root. */
  currentUserUpn: string | null;
  /** Programmatic pan helper from useZoomPan. */
  panTo: (x: number, y: number) => void;
  /** Programmatic zoom helper from useZoomPan. */
  zoomTo: (k: number) => void;
  /** Width of the (fixed) SVG viewBox in logical units, used to centre the
   *  target horizontally. */
  viewportWidth: number;
  /** Height of the (fixed) SVG viewBox in logical units, used for the top
   *  bias. */
  viewportHeight: number;
  /** Gates the effect — false while the SVG ref / zoom behaviour have not
   *  attached yet. Once true, the effect arms. */
  isReady: boolean;
}

/** Build a stable string that changes when the laid-out positions change so
 *  we can key the "already-focused" cache on it. We only need a coarse
 *  signature — the node count plus the root id is enough to detect a layout
 *  swap caused by data refresh or collapse/expand. */
function layoutSignature(
  layout: HierarchyPointNode<IOrgGraphNode> | null,
): string {
  if (layout === null) return 'null';
  const descendants = layout.descendants();
  return `${layout.data.id}:${descendants.length}`;
}

/**
 * Find the descendant matching `upn`. Returns null when not present (e.g.
 * the current user is an orphan we dropped, or hasn't been hired yet in
 * the dataset).
 */
function findUserNode(
  layout: HierarchyPointNode<IOrgGraphNode> | null,
  upn: string | null,
): HierarchyPointNode<IOrgGraphNode> | null {
  if (layout === null || upn === null) return null;
  return layout.descendants().find((d) => d.data.id === upn) ?? null;
}

/**
 * The node to frame when there's no current user. When the layout root is the
 * synthetic virtual root (multiple real roots — see PRODOPSAI-474), frame the
 * child with the largest subtree (the primary org branch) rather than the
 * virtual root's geometric centre, which would sit between unrelated trees.
 * Otherwise frame the root itself.
 */
function primaryRoot(
  layout: HierarchyPointNode<IOrgGraphNode>,
): HierarchyPointNode<IOrgGraphNode> {
  const children = layout.children ?? [];
  if (layout.data.id !== VIRTUAL_ROOT_ID || children.length === 0) {
    return layout;
  }
  return children.reduce((best, child) =>
    child.descendants().length > best.descendants().length ? child : best,
  );
}

/**
 * Pans + zooms the org tree once per `(currentUserUpn, layout-signature)`
 * pair so the signed-in user's card lands at the top-centre of the viewport.
 */
export function useTreeFocus({
  layout,
  currentUserUpn,
  panTo,
  zoomTo,
  viewportWidth,
  viewportHeight,
  isReady,
}: UseTreeFocusParams): void {
  // Key the cache on the combination of currentUserUpn + layout signature.
  // We use a ref so re-renders don't reset the "already focused" flag and
  // start fighting the user's manual pan/zoom.
  const lastFocusedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || layout === null) return;

    // Frame the current user when known; otherwise fall back to the primary
    // root (largest org branch) so the initial view is always anchored on a
    // meaningful subtree (the signed-in admin isn't always present in the
    // Entra org, and the export can have several outside-scope roots).
    const target = findUserNode(layout, currentUserUpn) ?? primaryRoot(layout);
    const focusUpn = currentUserUpn ?? `__root__:${target.data.id}`;

    const key = `${focusUpn}::${layoutSignature(layout)}`;
    if (lastFocusedKeyRef.current === key) return;

    // Apply zoom first so panTo's centre maths use the intended scale.
    // With a fixed viewBox, a logical point P maps to screen S = P*k +
    // translate, so to land P at viewport coordinate V we set
    // translate = V - P*k. We centre horizontally and bias toward the top.
    zoomTo(FOCUS_ZOOM);
    panTo(
      viewportWidth / 2 - target.x * FOCUS_ZOOM,
      viewportHeight * TOP_BIAS - target.y * FOCUS_ZOOM,
    );

    lastFocusedKeyRef.current = key;
  }, [layout, currentUserUpn, panTo, zoomTo, viewportWidth, viewportHeight, isReady]);
}
